import React, { useEffect, useState } from 'react';

const TeamModal = ({ team, onClose, onSave, isEdit }) => {
  const [form, setForm] = useState({
    name: team?.name || '',
  });
  const [loading, setLoading] = useState(false);
  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    await onSave(form);
    setLoading(false);
    onClose();
  };
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.4)', backdropFilter: 'blur(8px)', padding: '1rem' }} onClick={onClose}>
      <div style={{ position: 'relative', padding: '2rem', background: 'var(--color-bg)', width: '100%', maxWidth: 480, margin: 'auto', borderRadius: 'var(--border-radius)', boxShadow: 'var(--box-shadow)', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--color-primary)', marginBottom: '1rem' }}>{isEdit ? 'Edit Team' : 'Add Team'}</h3>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 500, color: 'var(--color-text)', marginBottom: 4 }}>Team Name</label>
            <input name="name" type="text" style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--border-color)', borderRadius: 'var(--border-radius)', color: 'var(--color-text)', background: 'var(--color-bg)', fontSize: '1rem' }} value={form.name} onChange={handleChange} required />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1.5rem' }}>
            <button type="button" onClick={onClose} style={{ background: 'var(--color-secondary)', color: 'var(--color-text-inverse)', border: 'none', borderRadius: 'var(--border-radius)', padding: '0.5rem 1.25rem', fontSize: '1rem', cursor: 'pointer' }}>Cancel</button>
            <button type="submit" disabled={loading} style={{ background: 'var(--color-primary)', color: 'var(--color-text-inverse)', border: 'none', borderRadius: 'var(--border-radius)', padding: '0.5rem 1.25rem', fontSize: '1rem', cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>{loading ? 'Saving...' : (isEdit ? 'Save Changes' : 'Add Team')}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TeamsPage = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editTeam, setEditTeam] = useState(null);

  // Fetch teams from backend
  const fetchTeams = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/get-teams.php');
      const data = await res.json();
      setTeams(Array.isArray(data) ? data : (data.teams || []));
    } catch (err) {
      setError('Failed to load teams.');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTeams();
  }, []);

  // Add team
  const handleAddTeam = async (teamData) => {
    const res = await fetch('/api/add-team.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(teamData)
    });
    if (!res.ok) throw new Error('Failed to add team');
    await fetchTeams();
  };

  // Edit team
  const handleEditTeam = async (editData) => {
    const res = await fetch('/api/edit-team.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: editTeam.id, ...editData })
    });
    if (!res.ok) throw new Error('Failed to edit team');
    await fetchTeams();
  };

  // Delete team
  const handleDeleteTeam = async (teamId) => {
    const res = await fetch('/api/delete-team.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: teamId })
    });
    if (!res.ok) throw new Error('Failed to delete team');
    await fetchTeams();
  };

  return (
    <div style={{ padding: 'var(--section-padding)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2 style={{ color: 'var(--color-primary)', fontSize: '2rem', fontWeight: 700 }}>Teams</h2>
        <button
          style={{
            background: 'var(--color-primary)',
            color: 'var(--color-text-inverse)',
            border: 'none',
            borderRadius: 'var(--border-radius)',
            padding: '0.5rem 1.5rem',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: 'var(--box-shadow)'
          }}
          onClick={() => { setEditTeam(null); setShowModal(true); }}
        >
          + Add Team
        </button>
      </div>
      {loading ? (
        <div>Loading teams...</div>
      ) : error ? (
        <div style={{ color: 'var(--color-error)' }}>{error}</div>
      ) : (
        <div style={{ background: 'var(--color-surface)', borderRadius: 'var(--border-radius)', boxShadow: 'var(--box-shadow)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg-alt)' }}>
                <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--color-text-light)' }}>Name</th>
                <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--color-text-light)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {teams.length === 0 ? (
                <tr><td colSpan={2} style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-light)' }}>No teams found.</td></tr>
              ) : (
                teams.map(team => (
                  <tr key={team.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '1rem', color: 'var(--color-text)' }}>{team.name}</td>
                    <td style={{ padding: '1rem' }}>
                      <button
                        style={{ background: 'var(--color-secondary)', color: 'var(--color-text-inverse)', border: 'none', borderRadius: 'var(--border-radius)', padding: '0.25rem 1rem', marginRight: 8, cursor: 'pointer' }}
                        onClick={() => { setEditTeam(team); setShowModal(true); }}
                      >
                        Edit
                      </button>
                      <button
                        style={{ background: 'var(--color-error)', color: 'var(--color-text-inverse)', border: 'none', borderRadius: 'var(--border-radius)', padding: '0.25rem 1rem', cursor: 'pointer' }}
                        onClick={() => handleDeleteTeam(team.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      {showModal && (
        <TeamModal
          team={editTeam}
          isEdit={!!editTeam}
          onClose={() => setShowModal(false)}
          onSave={editTeam ? handleEditTeam : handleAddTeam}
        />
      )}
    </div>
  );
};

export default TeamsPage; 